import * as React from "react";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MessageIcon from "@mui/icons-material/Message";
import {
  AppBar,
  IconButton,
  Typography,
  Box,
  MenuItem,
  Menu,
  Badge,
} from "@mui/material";
import {
  TitlePortal,
  RefreshIconButton,
  useLogout,
  useStore,
  useGetIdentity,
  useGetOne,
} from "react-admin";
import Parse from "parse";
import { Loader } from "../Views/Loader";
import GlobalSettingsDialog from "../Modals/GlobalSettingsDialog";
import ChatbotDialog from "../Modals/ChatbotDialog";
import MessagingWidget from "../Views/Messaging/MessagingWidget";
import MessagingSettingsDialog from "../Views/Messaging/MessagingSettingsDialog";

const drawerWidth = "16em";
const collapsedWidth = "0em";

export default function MyAppBar({ open, toggleDrawer }) {
  const { data } = useGetIdentity();
  const logout = useLogout();
  const [loading, setLoading] = React.useState(false);
  const [globalDialogOpen, setGlobalDialogOpen] = React.useState(false);
  const [chatbotDialogOpen, setChatbotDialogOpen] = React.useState(false);
  const [messagingOpen, setMessagingOpen] = React.useState(false);
  const [messagingSettingsOpen, setMessagingSettingsOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [selectedMessagingUser, setSelectedMessagingUser] = React.useState(null);
  const [messagingEnabled, setMessagingEnabled] = React.useState(true);

  // Check if messaging is enabled
  React.useEffect(() => {
    const checkMessagingStatus = async () => {
      try {
        const query = new Parse.Query('GameSettings');
        const settings = await query.first({ useMasterKey: true });
        if (settings) {
          setMessagingEnabled(settings.get('messaging') === true);
        }
      } catch (error) {
        console.error('Error checking messaging status:', error);
      }
    };
    
    checkMessagingStatus();
  }, []);

  // Expose global function to open messaging with a specific user
  React.useEffect(() => {
    window.openMessagingWithUser = (user) => {
      setSelectedMessagingUser(user);
      setMessagingOpen(true);
    };

    return () => {
      delete window.openMessagingWithUser;
    };
  }, []);

  const { data: userData } = useGetOne(
    "users",
    { id: data?.userId },
    { enabled: !!data?.userId }
  );

  const [userBalance, setUserBalance] = useStore("userBalance", 0);
  const [userBalanceCall, setUserBalanceCall] = useStore(
    "userBalanceCall",
    false
  );

  const [anchorEl, setAnchorEl] = React.useState(null);
  const menuOpen = Boolean(anchorEl);

  React.useEffect(() => {
    if (userData?.balance !== undefined) {
      setUserBalance(userData?.balance);
      setUserBalanceCall(false);
    }
  }, [userData?.balance]);

  // Load unread message count
  React.useEffect(() => {
    const loadUnreadCount = async () => {
      if (!messagingEnabled) return;
      
      try {
        const result = await Parse.Cloud.run('getUnreadMessageCount');
        setUnreadCount(result.unreadCount || 0);
      } catch (error) {
        console.error('Error loading unread count:', error);
      }
    };

    if (data?.userId && messagingEnabled) {
      loadUnreadCount();
      // Poll for unread count every 30 seconds
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [data?.userId, messagingEnabled]);

  // Socket.io listener for new messages
  React.useEffect(() => {
    if (window.socket && data?.userId && messagingEnabled) {
      const handleMessageReceived = (payload) => {
        const msgData = typeof payload === 'string' ? JSON.parse(payload) : payload;
        setUnreadCount(prev => prev + 1);
        
        // Show browser notification if supported
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('New Message', {
            body: `${msgData.senderUsername || 'Someone'} sent you a message`,
            icon: '/Assets/Logos/logo.png',
            tag: 'message-notification'
          });
        }
      };

      window.socket.on('message_received', handleMessageReceived);
      
      // Request notification permission on first load
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      
      return () => {
        window.socket.off('message_received', handleMessageReceived);
      };
    }
  }, [data?.userId, messagingEnabled]);

  const handleLogout = () => {
    setLoading(true);
    logout();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <AppBar
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: 1,
        alignItems: "center",
        paddingRight: "1em",
        background: "linear-gradient(0deg, #242424 0%, #121212 83.59%)",
        boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
        position: "fixed",
        left: open ? drawerWidth : collapsedWidth,
        top: 0,
        right: 0,
        width: `calc(100% - ${open ? drawerWidth : collapsedWidth})`,
        height: "4em",
        color: "#fff",
        transition: "width 0.3s ease, left 0.3s ease",
      }}
    >
      {/* Sidebar Toggle */}
      <IconButton
        onClick={toggleDrawer}
        sx={[{ color: "#fff", marginLeft: 1 }, open && { display: "none" }]}
      >
        <MenuIcon />
      </IconButton>

      <TitlePortal variant="h5" component="h3" sx={{ paddingLeft: 3 }} />

      <Box>
        {data && data?.userRoleName !== "Super-User" && (
          <Typography>Balance: {userBalance?.toFixed(2)}</Typography>
        )}
      </Box>

      <RefreshIconButton />

      {/* Messaging Icon - Only show if messaging is enabled */}
      {messagingEnabled && (
        <IconButton
          color="inherit"
          onClick={() => {
            setMessagingOpen(true);
            setUnreadCount(0); // Reset count when opening
          }}
          title="Messages"
        >
          <Badge badgeContent={unreadCount} color="error">
            <MessageIcon />
          </Badge>
        </IconButton>
      )}

      {loading ? (
        <Loader />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography>{data?.username}</Typography>
          <IconButton
            onClick={handleMenuClick}
            size="large"
            color="inherit"
            sx={{ p: 0 }}
          >
            <AccountCircleIcon fontSize="medium" />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {/* { process.env.REACT_APP_ENV === "development" && data?.userRoleName === "Super-User" && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setGlobalDialogOpen(true);
                }}
              >
                Recharge & Redeem
              </MenuItem>
            )} */}
              {data?.userRoleName === "Super-User" && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setChatbotDialogOpen(true);
                }}
              >
                Chatbot Settings
              </MenuItem>
            )}
            {data?.userRoleName === "Super-User" && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setMessagingSettingsOpen(true);
                }}
              >
                Messaging Settings
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleLogout();
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Box>
      )}

      <GlobalSettingsDialog
        open={globalDialogOpen}
        onClose={() => setGlobalDialogOpen(false)}
      />
      
      <ChatbotDialog
        open={chatbotDialogOpen}
        onClose={() => setChatbotDialogOpen(false)}
      />

      <MessagingWidget
        open={messagingOpen}
        onClose={() => {
          setMessagingOpen(false);
          setSelectedMessagingUser(null);
        }}
        selectedUser={selectedMessagingUser}
      />

      <MessagingSettingsDialog
        open={messagingSettingsOpen}
        onClose={() => {
          setMessagingSettingsOpen(false);
          // Refresh messaging status after closing settings
          Parse.Query.prototype.first.call(
            new Parse.Query('GameSettings'),
            { useMasterKey: true }
          ).then(settings => {
            if (settings) {
              setMessagingEnabled(settings.get('messaging') === true);
            }
          });
        }}
      />
    </AppBar>
  );
}
